import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext'
import { Shield, AlertTriangle } from 'lucide-react';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredPermission?: string;
  requiredRole?: string;
  requirePlatformAdmin?: boolean;
}

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  requiredPermission,
  requiredRole,
  requirePlatformAdmin = false,
}) => {
  const { user, isAuthenticated, isLoading, checkPermission, hasPermission, hasRole } = useAuth();
  const location = useLocation();

  // Show loading spinner while checking auth
  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-console-muted">Checking permissions...</p>
        </div>
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated || !user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Check platform admin requirement
  if (requirePlatformAdmin) {
    const isPlatformAdmin = checkPermission('system', 'admin') || 
                           checkPermission('system', 'tenants:read');
    
    if (!isPlatformAdmin) {
      return (
        <div className="flex items-center justify-center min-h-96">
          <div className="text-center max-w-md">
            <Shield className="w-16 h-16 text-console-muted mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-console-text mb-2">Platform Admin Required</h2>
            <p className="text-console-muted mb-6">
              This section requires platform administrator privileges. Contact your system administrator for access.
            </p>
            <button 
              onClick={() => window.history.back()}
              className="btn btn-primary"
            >
              Go Back
            </button>
          </div>
        </div>
      );
    }
  }

  // Check specific permission. Permission strings use the dot-delimited format
  // emitted by the socket-cloud RBAC API (e.g. "tenant.users.view"). Fall back
  // to the legacy "resource:action" form for backward compatibility.
  if (requiredPermission) {
    const allowed = requiredPermission.includes(':')
      ? (() => {
          const [resource, action] = requiredPermission.split(':');
          return checkPermission(resource, action);
        })()
      : hasPermission(requiredPermission);
    if (!allowed) {
      return (
        <div className="flex items-center justify-center min-h-96">
          <div className="text-center max-w-md">
            <AlertTriangle className="w-16 h-16 text-warning-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-console-text mb-2">Access Denied</h2>
            <p className="text-console-muted mb-6">
              You don&apos;t have the required permission: <code className="bg-console-surface px-2 py-1 rounded text-sm">{requiredPermission}</code>
            </p>
            <button 
              onClick={() => window.history.back()}
              className="btn btn-primary"
            >
              Go Back
            </button>
          </div>
        </div>
      );
    }
  }

  // Check specific role
  if (requiredRole && !hasRole(requiredRole)) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="text-center max-w-md">
          <Shield className="w-16 h-16 text-console-muted mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-console-text mb-2">Insufficient Role</h2>
          <p className="text-console-muted mb-6">
            This section requires the <strong>{requiredRole}</strong> role.
          </p>
          <button 
            onClick={() => window.history.back()}
            className="btn btn-primary"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  // User has required permissions, render the protected content
  return <>{children}</>;
};

// Convenience components for common protection scenarios
export const PlatformAdminRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <ProtectedRoute requirePlatformAdmin>
    {children}
  </ProtectedRoute>
);

export const PermissionRoute: React.FC<{ 
  children: React.ReactNode; 
  permission: string;
}> = ({ children, permission }) => (
  <ProtectedRoute requiredPermission={permission}>
    {children}
  </ProtectedRoute>
);

export const RoleRoute: React.FC<{ 
  children: React.ReactNode; 
  role: string;
}> = ({ children, role }) => (
  <ProtectedRoute requiredRole={role}>
    {children}
  </ProtectedRoute>
);